import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowUpRight, Search, Tag } from 'lucide-react';
import { PublicPageLayout } from '../components/PublicPageLayout';
import { api } from '../services/api';

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 28px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--bg-card);
  border: 1px solid rgba(0, 212, 170, 0.12);
  border-radius: 999px;
  padding: 10px 16px;
  flex: 1;
  min-width: 240px;
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  color: var(--text-light);
  width: 100%;
  font-size: 0.95rem;
  outline: none;
`;

const TagButton = styled.button<{ $active?: boolean }>`
  border-radius: 999px;
  border: 1px solid rgba(0, 212, 170, 0.25);
  padding: 8px 14px;
  background: ${props => props.$active ? 'var(--gradient-primary)' : 'transparent'};
  color: ${props => props.$active ? '#0a0e17' : 'var(--text-muted)'};
  font-weight: 600;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const Featured = styled.div`
  background: linear-gradient(135deg, rgba(0, 212, 170, 0.18), rgba(8, 145, 178, 0.12));
  border-radius: 20px;
  padding: 28px;
  border: 1px solid rgba(0, 212, 170, 0.2);
  margin-bottom: 24px;
  display: grid;
  gap: 12px;
`;

const Card = styled.article`
  background: var(--bg-card);
  border-radius: 18px;
  padding: 22px;
  border: 1px solid rgba(0, 212, 170, 0.12);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CoverImage = styled.div<{ $src?: string }>`
  height: 160px;
  border-radius: 14px;
  background: ${props => props.$src
    ? `linear-gradient(180deg, rgba(10,14,23,0) 0%, rgba(10,14,23,0.4) 100%), url(${props.$src})`
    : 'linear-gradient(135deg, rgba(0, 212, 170, 0.2), rgba(8, 145, 178, 0.2))'};
  background-size: cover;
  background-position: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--primary);
  background: rgba(0, 212, 170, 0.12);
  padding: 4px 10px;
  border-radius: 999px;
  width: fit-content;
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
`;

const Excerpt = styled.p`
  color: var(--text-muted);
  font-size: 0.95rem;
`;

const Meta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 0.8rem;
  color: #94a3b8;
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--primary);
  background: none;
  border: none;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

type BlogPostData = {
  id: number;
  title: string;
  slug?: string;
  excerpt?: string;
  category?: string;
  date?: string;
  readTime?: string;
  content?: string[] | string;
  tags?: string[];
  cover_image?: string;
  published_at?: string;
  created_at?: string;
};

export const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPostData[]>([]);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('Todos');

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await api.get('/blog/posts');
        if (response.data?.success && Array.isArray(response.data.data)) {
          setPosts(response.data.data);
        }
      } catch {
        setPosts([]);
      }
    };

    loadPosts();
  }, []);

  const estimateReadTime = (content: string) => {
    const words = content.split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.round(words / 180));
    return `${minutes} min`;
  };

  const tags = useMemo(() => {
    const derived = posts.map(post => post.category || post.tags?.[0] || 'Geral');
    return ['Todos', ...Array.from(new Set(derived))];
  }, [posts]);

  const filtered = useMemo(() => {
    return posts.filter(post => {
      const postTag = post.category || post.tags?.[0] || 'Geral';
      const matchesTag = activeTag === 'Todos' || postTag === activeTag;
      const excerpt = post.excerpt || '';
      const matchesSearch =
        post.title.toLowerCase().includes(search.toLowerCase()) ||
        excerpt.toLowerCase().includes(search.toLowerCase());
      return matchesTag && matchesSearch;
    });
  }, [activeTag, search, posts]);

  const featured = filtered[0] || posts[0];
  const getReadTime = (post: any) => post.readTime || estimateReadTime(post.content || post.excerpt || '');
  const openPost = (post: BlogPostData) => {
    if (!post.slug) return;
    navigate(`/blog/${post.slug}`);
  };

  return (
    <PublicPageLayout
      title="Blog PontoFiel"
      subtitle="Insights, estrategias e noticias para acelerar o crescimento das lojas."
      eyebrow="Conteudo exclusivo"
    >
      {featured && (
        <Featured>
          <Badge><Tag size={14} /> {featured.category || featured.tags?.[0] || 'Geral'}</Badge>
          <CoverImage $src={featured.cover_image} />
          <Title>{featured.title}</Title>
          <Excerpt>{featured.excerpt || 'Conteudo estrategico para crescer com o PontoFiel.'}</Excerpt>
          <Meta>
            <span>{featured.date || new Date(featured.published_at || featured.created_at || Date.now()).toLocaleDateString('pt-BR')}</span>
            <span>{getReadTime(featured)}</span>
          </Meta>
          <LinkButton onClick={() => openPost(featured)}>
            Ler artigo <ArrowUpRight size={16} />
          </LinkButton>
        </Featured>
      )}

      <FiltersRow>
        <SearchBox>
          <Search size={18} />
          <SearchInput
            placeholder="Buscar artigos..."
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </SearchBox>
        {tags.map(tag => (
          <TagButton key={tag} $active={tag === activeTag} onClick={() => setActiveTag(tag)}>
            {tag}
          </TagButton>
        ))}
      </FiltersRow>

      <Grid>
        {filtered.length === 0 ? (
          <Card>
            <Title>Nenhum post encontrado</Title>
            <Excerpt>Quando novos artigos forem publicados, eles aparecerao aqui.</Excerpt>
          </Card>
        ) : (
          filtered.map(post => (
            <Card key={post.id}>
              <CoverImage $src={post.cover_image} />
              <Badge><Tag size={14} /> {post.category || post.tags?.[0] || 'Geral'}</Badge>
              <Title>{post.title}</Title>
              <Excerpt>{post.excerpt || 'Conteudo estrategico para crescer com o PontoFiel.'}</Excerpt>
              <Meta>
                <span>{post.date || new Date(post.published_at || post.created_at || Date.now()).toLocaleDateString('pt-BR')}</span>
                <span>{getReadTime(post)}</span>
              </Meta>
              <LinkButton onClick={() => openPost(post)}>
                Ler artigo <ArrowUpRight size={16} />
              </LinkButton>
            </Card>
          ))
        )}
      </Grid>
    </PublicPageLayout>
  );
};
